/**
 * Utilidad centralizada para el manejo de errores provenientes del API Swagger
 * 
 * Este módulo proporciona funciones para extraer información útil de los errores
 * generados por las llamadas al API y convertirlos en mensajes amigables para el usuario.
 */

import { errorLogService } from "../services/errorLogService";

/**
 * Extrae el mensaje de error de una respuesta del API
 * @param {Object} error - El objeto error del callback
 * @param {Object} response - El objeto response del callback
 * @returns {Object} Objeto con statusCode, message y error
 */
export const parseApiError = (error, response) => {
  const result = {
    statusCode: null,
    message: "Ha ocurrido un error inesperado",
    error: null,
    isNetworkError: false,
    isAuthError: false
  };

  // Si hay un response, extraer el statusCode
  if (response) {
    result.statusCode = response.statusCode || response.status;
  }

  // Si hay error y tiene información de response
  if (error) {
    // Intentar extraer información del body de la respuesta
    if (error.response && error.response.body) {
      const body = error.response.body;
      
      // El API puede devolver ErrorDto (statusCode, error, message)
      // o ErrorResponse (status, message)
      if (body.message) {
        result.message = body.message;
      }
      
      if (body.error) {
        result.error = body.error;
      }
      
      if (body.statusCode) {
        result.statusCode = body.statusCode;
      } else if (body.status) {
        result.statusCode = body.status;
      }
    }
    
    // Si el error tiene un status directamente
    if (error.status) {
      result.statusCode = error.status;
    }
    
    // Si el error tiene un mensaje directo
    if (error.message && !result.message) {
      result.message = error.message;
    }

    // Detectar errores de red
    if (error.message && (error.message.includes("Network") || error.message.includes("timeout"))) {
      result.isNetworkError = true;
      result.message = "Error de conexión. Por favor, verifica tu conexión a internet.";
    }
  }

  // Marcar errores de autenticación
  if (result.statusCode === 401 || result.statusCode === 403) {
    result.isAuthError = true;
  }

  return result;
};

/**
 * Genera un mensaje de error amigable basado en el código de estado
 * @param {Object} error - El objeto error del callback
 * @param {Object} response - El objeto response del callback
 * @param {string} defaultMessage - Mensaje por defecto si no se puede determinar uno específico
 * @returns {string} Mensaje de error amigable
 */
export const getErrorMessage = (error, response, defaultMessage = "Ha ocurrido un error") => {
  const parsed = parseApiError(error, response);
  
  // Si tenemos un mensaje específico del servidor, usarlo
  if (parsed.message && parsed.message !== "Ha ocurrido un error inesperado") {
    return parsed.message;
  }

  // Mensajes por defecto según el código de estado
  switch (parsed.statusCode) {
    case 400:
      return "Solicitud inválida. Por favor, revisa los datos ingresados.";
    case 401:
      return "No autorizado. Por favor, inicia sesión nuevamente.";
    case 403:
      return "No tienes permisos para realizar esta acción.";
    case 404:
      return "Recurso no encontrado.";
    case 409:
      return "Esta invitación ya ha sido procesada.";
    case 422:
      return "Datos inválidos. Por favor, verifica la información ingresada.";
    case 429:
      return "Demasiados intentos. Por favor, intenta más tarde.";
    case 500:
      return "Error interno del servidor. Por favor, inténtalo más tarde.";
    case 502:
      return "Servicio temporalmente no disponible. Por favor, inténtalo más tarde.";
    case 503:
      return "Servicio en mantenimiento. Por favor, inténtalo más tarde.";
    default:
      return parsed.isNetworkError ? parsed.message : defaultMessage;
  }
};

/**
 * Verifica si una respuesta del API es exitosa
 * @param {Object} error - El objeto error del callback
 * @param {Object} response - El objeto response del callback
 * @param {number} expectedStatus - Código de estado esperado (por defecto 200)
 * @returns {boolean} true si la respuesta es exitosa, false en caso contrario
 */
export const isApiSuccess = (error, response, expectedStatus = 200) => {
  if (error) return false;
  if (!response) return false;
  
  const statusCode = response.statusCode || response.status;
  return statusCode === expectedStatus;
};

/**
 * Wrapper para callbacks del API que maneja errores automáticamente
 * @param {Function} onSuccess - Función a ejecutar si la llamada es exitosa
 * @param {Function} onError - Función a ejecutar si hay error (recibe el mensaje de error)
 * @param {number} expectedStatus - Código de estado esperado (por defecto 200)
 * @returns {Function} Callback function compatible con el API
 */
export const createApiCallback = (onSuccess, onError, expectedStatus = 200) => {
  return (error, data, response) => {
    if (isApiSuccess(error, response, expectedStatus)) {
      onSuccess(data, response);
    } else {
      const errorMessage = getErrorMessage(error, response);
      const errorInfo = parseApiError(error, response);
      
      // Registrar error en el sistema de logs
      if (error) {
        const requestUrl = response?.req?.url || response?.url || window.location.href;
        const httpMethod = response?.req?.method || "UNKNOWN";
        const httpStatusCode = errorInfo.statusCode;
        
        errorLogService.logApiError(
          error instanceof Error ? error : new Error(errorMessage),
          httpMethod,
          requestUrl,
          httpStatusCode
        );
      }
      
      onError(errorMessage, errorInfo, error, response);
    }
  };
};

/**
 * Logs de error para debugging (solo en desarrollo)
 * @param {string} context - Contexto donde ocurrió el error
 * @param {Object} error - El objeto error
 * @param {Object} response - El objeto response
 */
export const logApiError = (context, error, response) => {
  if (process.env.NODE_ENV === "development") {
    console.group(`API Error - ${context}`);
    console.error("Error object:", error);
    console.error("Response object:", response);
    console.error("Parsed error:", parseApiError(error, response));
    console.groupEnd();
  }
};
